import React from 'react';

import { Box, Typography } from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import delay from 'lodash/delay';

import {
  ErrorText,
  FileIconWrapper,
  IconWrapper,
  UploadAccordion,
  UploadAccordionHeader,
  UploadBox,
  UploadDetails,
  UploadHeaderLeft,
  UploadHeaderRight,
  UploadItem,
  UploadItemLeft,
  UploadItemProgress,
  UploadItemRow,
  UploadItemText,
} from '@pages/organizer/Cloud/components/CloudUploadPopup/CloudUploadPopup.style';
import { UPLOAD_STATUS_ENUM } from '@pages/organizer/Cloud/const/upload/upload-status';
import {
  expandVisiblePopup,
  progressState,
  selectSortedUploads,
  uploadCountsSelector,
} from '@pages/organizer/Cloud/store/upload-store/upload-selectors';
import {
  abortAll,
  abortUpload,
  expandPopup,
  resetUploadState,
  visiblePopup,
} from '@pages/organizer/Cloud/store/upload-store/upload-slice';
import {
  ArrowDownIcon,
  CrossIcon,
  ErrorOutlineIcon,
  FileIcon,
  RoundDoneIcon,
} from '@setup/svg-icons/svg-icons';
import { AlertModalVariantEnum } from '@shared/configs/modals/alertModal/alertModal.config';
import { ModalNamesEnum } from '@shared/enums/modalNames.enum';
import { useAppDispatch, useAppSelector } from '@shared/hooks/redux';
import { MuiBadge, MuiButton, MuiIconButton } from '@shared/ui-kit';
import modalObserver from '@shared/utils/observers/modalObserver';

import type { PreviewFileType, UploadCounts } from '@pages/organizer/Cloud/store/upload-store/upload-types';

const count = (counts: UploadCounts, uploads: PreviewFileType[]) => {
  const {
    total_upload_files,
    uploaded_files,
    is_upload_complete,
    failed_upload_count,
    canceled_upload_count,
  } = counts;
  const countUploads = uploads?.length ?? 0;

  if (is_upload_complete) {
    if (failed_upload_count > 0) {
      return failed_upload_count <= 1
        ? `${failed_upload_count} upload failed`
        : `${failed_upload_count} uploads failed`;
    }
    if (canceled_upload_count > 0) {
      return canceled_upload_count <= 1
        ? `${canceled_upload_count} upload canceled`
        : `${canceled_upload_count} uploads canceled`;
    }

    return `${countUploads} uploads completed`;
  }

  return `Uploading ${total_upload_files - uploaded_files} items`;
};

const UploadPopup = () => {
  const dispatch = useAppDispatch();
  const progress = useAppSelector(progressState);
  const expand = useAppSelector(expandVisiblePopup);
  const uploads = useAppSelector(selectSortedUploads);
  const uploadCounts = useAppSelector(uploadCountsSelector);

  const resultCount = count(uploadCounts, uploads);

  const handleOpen = () => {
    dispatch(expandPopup({ expand: !expand }));
  };

  const handleClose = () => {
    dispatch(expandPopup({ expand: false }));

    delay(() => {
      dispatch(visiblePopup({ visible: false }));
      dispatch(resetUploadState());
    }, 200);
  };

  const handleAbortUploadFile = (preview_id: string) => {
    dispatch(abortUpload(preview_id));
  };

  const handleAbortAllUploadFiles = () => {
    modalObserver.addModal(ModalNamesEnum.alertModal, {
      props: {
        modalContent: {
          header: 'Cancel upload',
          isBoxShadow: false,
          title: 'Are you sure you want to cancel the upload?',
        },
        rightBtnProps: {
          isShow: true,
          label: 'Cancel upload',
          size: 'medium',
          color: 'error',
          onClick: () => {
            dispatch(abortAll());
            modalObserver.removeModal(ModalNamesEnum.alertModal);
          },
        },
        middleBtnProps: {
          isShow: true,
          color: 'secondary',
          label: 'Continue upload',
          size: 'medium',
          onClick: () => {
            modalObserver.removeModal(ModalNamesEnum.alertModal);
          },
        },
        variant: AlertModalVariantEnum.delete,
      },
    });
  };

  return (
    <UploadBox>
      <UploadAccordion expanded={expand}>
        <UploadAccordionHeader>
          <UploadHeaderLeft>
            <MuiIconButton onClick={handleOpen} size="medium">
              <IconWrapper expand={expand}>
                <ArrowDownIcon />
              </IconWrapper>
            </MuiIconButton>
            <Typography
              variant="t14m"
              color={(theme) => theme.palette.case.neutral.n900}
              onClick={handleOpen}
            >
              {resultCount}
            </Typography>

            {!!uploadCounts.failed_upload_count && !expand && (
              <Box ml="4px" display="flex">
                <MuiBadge color="error" variant="dot" size="small" />
              </Box>
            )}
          </UploadHeaderLeft>

          <UploadHeaderRight>
            {uploadCounts.is_upload_complete ? (
              <MuiIconButton onClick={handleClose} size="small">
                <CrossIcon />
              </MuiIconButton>
            ) : (
              <MuiButton
                color="secondary"
                size="small"
                variant="text"
                label="Cancel All"
                onClick={handleAbortAllUploadFiles}
              />
            )}
          </UploadHeaderRight>
        </UploadAccordionHeader>

        <UploadDetails>
          {uploads.map((file: PreviewFileType) => (
            <UploadItem key={file.preview_id}>
              <UploadItemRow>
                <UploadItemLeft>
                  <FileIconWrapper>
                    <FileIcon />
                  </FileIconWrapper>

                  <UploadItemText isCancel={file.is_canceled} noWrap variant="t14m">
                    {file.name}.{file.extension}
                  </UploadItemText>
                </UploadItemLeft>

                {file.status === UPLOAD_STATUS_ENUM.progress && (
                  <MuiIconButton size="small" onClick={() => handleAbortUploadFile(file.preview_id)}>
                    <CrossIcon />
                  </MuiIconButton>
                )}
                {file.status === UPLOAD_STATUS_ENUM.completed && (
                  <Box sx={{ svg: { width: '20px', height: '20px' } }}>
                    <RoundDoneIcon />
                  </Box>
                )}
                {[UPLOAD_STATUS_ENUM.error].includes(file.status) && (
                  <Box sx={{ svg: { width: '20px', height: '20px' } }}>
                    <ErrorOutlineIcon />
                  </Box>
                )}
                {[UPLOAD_STATUS_ENUM.abort].includes(file.status) && (
                  <Box>
                    <Typography
                      alignSelf="center"
                      color={(theme) => theme.palette.case.neutral.n600}
                      variant="t14r"
                    >
                      Canceled
                    </Typography>
                  </Box>
                )}
              </UploadItemRow>

              {![UPLOAD_STATUS_ENUM.error, UPLOAD_STATUS_ENUM.abort].includes(file.status) && (
                <UploadItemProgress>
                  <LinearProgress variant="determinate" value={progress[file.preview_id] ?? 0} />
                </UploadItemProgress>
              )}

              {[UPLOAD_STATUS_ENUM.error].includes(file.status) && (
                <ErrorText noWrap variant="t12r">
                  Upload failed
                </ErrorText>
              )}
            </UploadItem>
          ))}
        </UploadDetails>
      </UploadAccordion>
    </UploadBox>
  );
};

export default UploadPopup;
